/**
 * AgentSelector - Dropdown para cambiar agente asignado en Wassenger
 * Estilo similar al selector de agentes de Wassenger CRM
 */

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { wassengerAPI } from '../../services/api'

// Colores de Wassenger mapeados a Tailwind
const WASSENGER_COLORS = {
  red: 'bg-red-500',
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  orange: 'bg-orange-500',
  yellow: 'bg-yellow-500',
  pink: 'bg-pink-500',
  azure: 'bg-cyan-500',
  gray: 'bg-gray-500',
  purple: 'bg-purple-500'
}

// Nombres virtuales (no son agentes reales)
const EXCLUDED_NAMES = [
  'bot wassenger',
  'ninguno'
]

// Cache global del equipo (evita refetch en cada instancia)
let teamCache = null
let teamCacheTime = 0
const CACHE_TTL = 5 * 60 * 1000

async function fetchTeamCached() {
  const now = Date.now()
  if (teamCache && (now - teamCacheTime) < CACHE_TTL) {
    return teamCache
  }
  const data = await wassengerAPI.getTeam()
  teamCache = data
  teamCacheTime = now
  return data
}

export default function AgentSelector({ phone, currentAgent, onAssign, mode = 'assign', dropdownDirection = 'down' }) {
  const [isOpen, setIsOpen] = useState(false)
  const [agents, setAgents] = useState([])
  const [loading, setLoading] = useState(false)
  const [assigning, setAssigning] = useState(false)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const dropdownRef = useRef(null)
  const triggerRef = useRef(null)

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          triggerRef.current && !triggerRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Cerrar al hacer scroll en el documento (no dentro del dropdown)
  useEffect(() => {
    const handleScroll = (e) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      window.addEventListener('scroll', handleScroll, true)
    }
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [isOpen])

  // Cargar agentes al abrir
  const handleOpen = async () => {
    const willOpen = !isOpen

    if (willOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const dropdownHeight = 280 // altura aproximada del dropdown

      if (dropdownDirection === 'up' || spaceBelow < dropdownHeight) {
        // Abrir hacia arriba
        setDropdownPos({
          top: rect.top - 4,
          left: rect.left,
          openUp: true
        })
      } else {
        // Abrir hacia abajo
        setDropdownPos({
          top: rect.bottom + 4,
          left: rect.left,
          openUp: false
        })
      }
    }

    setIsOpen(!isOpen)

    if (!isOpen && agents.length === 0) {
      setLoading(true)
      try {
        const team = await fetchTeamCached()
        // Filtrar: solo agentes reales (excluir virtuales y superAdmin)
        const filtered = team.filter(a => {
          const name = (a.displayName || '').toLowerCase()
          return !EXCLUDED_NAMES.some(ex => name.includes(ex))
        })
        setAgents(filtered)
      } catch (err) {
        console.error('Error cargando equipo:', err)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleAssign = async (agent) => {
    if (assigning) return
    // mode="select": solo notificar sin llamar API
    if (mode === 'select') {
      setIsOpen(false)
      if (onAssign) onAssign(agent.displayName, agent.id)
      return
    }
    setAssigning(true)
    try {
      const result = await wassengerAPI.assignAgent(phone, agent.id)
      setIsOpen(false)
      if (onAssign) onAssign(agent.displayName, agent.id, result?.warning)
    } catch (err) {
      console.error('Error asignando agente:', err)
    } finally {
      setAssigning(false)
    }
  }

  const handleUnassign = async () => {
    if (assigning) return
    // mode="select": solo notificar sin llamar API
    if (mode === 'select') {
      setIsOpen(false)
      if (onAssign) onAssign(null, null)
      return
    }
    setAssigning(true)
    try {
      const result = await wassengerAPI.unassignAgent(phone)
      setIsOpen(false)
      if (onAssign) onAssign(null, null, result?.warning)
    } catch (err) {
      console.error('Error desasignando:', err)
    } finally {
      setAssigning(false)
    }
  }

  const colorClass = (color) => WASSENGER_COLORS[color] || 'bg-gray-400'

  // Normalizar para comparar (quitar acentos)
  const normalize = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

  const isCurrentAgent = (agent) => {
    if (!currentAgent) return false
    return normalize(agent.displayName) === normalize(currentAgent)
  }

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
      style={
        dropdownPos.openUp
          ? { bottom: window.innerHeight - dropdownPos.top, left: dropdownPos.left }
          : { top: dropdownPos.top, left: dropdownPos.left }
      }
    >
      <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-100 dark:border-gray-600">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Asignar a</span>
      </div>

      {loading ? (
        <div className="p-4 text-center">
          <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <span className="text-xs text-gray-400 mt-2 block">Cargando equipo...</span>
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto pb-1">
          {/* Opcion: Sin asignar */}
          <button
            onClick={handleUnassign}
            disabled={assigning}
            className={`
              w-full px-3 py-2 text-left text-sm flex items-center gap-2.5 transition
              ${!currentAgent || currentAgent === 'Ninguno'
                ? 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-medium'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }
            `}
          >
            <span className="w-2.5 h-2.5 rounded-full bg-gray-300 flex-shrink-0" />
            <span className="uppercase text-sm">Sin asignar</span>
            {(!currentAgent || currentAgent === 'Ninguno') && (
              <svg className="w-3.5 h-3.5 ml-auto text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </button>

          {/* Lista de agentes */}
          {agents.map(agent => {
            const isCurrent = isCurrentAgent(agent)
            return (
              <button
                key={agent.id}
                onClick={() => handleAssign(agent)}
                disabled={assigning || isCurrent}
                className={`
                  w-full px-3 py-2 text-left text-sm flex items-center gap-2.5 transition
                  ${isCurrent
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }
                `}
              >
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${colorClass(agent.color)}`} />
                <span className="truncate uppercase text-sm">{agent.displayName}</span>
                <span className="text-[10px] text-gray-400 ml-auto flex-shrink-0">
                  {agent.role === 'admin' ? 'Admin' : ''}
                </span>
                {isCurrent && (
                  <svg className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )

  return (
    <div className="relative">
      {/* Boton que muestra agente actual */}
      <button
        ref={triggerRef}
        onClick={handleOpen}
        disabled={assigning}
        className={`
          inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-base font-medium
          transition-all duration-150 border
          ${currentAgent && currentAgent !== 'Ninguno'
            ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
            : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
          }
          ${assigning ? 'opacity-60 cursor-wait' : 'cursor-pointer'}
        `}
      >
        {assigning ? (
          <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        ) : (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        )}
        <span className="truncate max-w-[120px]">
          {currentAgent && currentAgent !== 'Ninguno' ? currentAgent : 'Sin asignar'}
        </span>
        <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown via portal (evita overflow-hidden del parent) */}
      {isOpen && createPortal(dropdownContent, document.body)}
    </div>
  )
}
